import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { LocationIcon, HeartIcon, AwardIcon, UsersIcon } from './icons/ModernIcons';

const About = () => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const features = [
    {
      icon: LocationIcon,
      title: 'Fränkisches Seenland',
      description: 'Verwurzelt in der wunderschönen Region um Pleinfeld'
    },
    {
      icon: HeartIcon,
      title: 'Mit Leidenschaft',
      description: 'Jedes Projekt wird mit Hingabe und Sorgfalt umgesetzt'
    },
    {
      icon: AwardIcon,
      title: 'Höchste Qualität',
      description: 'Modernste Technik für langlebige und hochwertige Ergebnisse'
    },
    {
      icon: UsersIcon,
      title: 'Persönlicher Service',
      description: 'Individuelle Beratung für Privatkunden und Unternehmen'
    }
  ];

  return (
    <section id="about" className="py-20 relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" 
          alt="Fränkisches Seenland"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/95 via-purple-900/90 to-blue-900/95"></div>
      </div>

      {/* Floating geometric shapes */}
      <div className="absolute inset-0">
        <motion.div
          animate={{ rotate: 360, scale: [1, 1.2, 1] }}
          transition={{ duration: 20, repeat: Infinity }}
          className="absolute top-20 right-20 w-32 h-32 border-2 border-white/20 rounded-full"
        />
        <motion.div
          animate={{ rotate: -360, scale: [1, 0.8, 1] }}
          transition={{ duration: 15, repeat: Infinity }}
          className="absolute bottom-20 left-20 w-24 h-24 border-2 border-blue-300/30 rounded-lg"
        />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                Über{' '}
                <span className="bg-gradient-to-r from-blue-300 to-purple-300 bg-clip-text text-transparent">
                  LIMA-Service
                </span>
              </h2>
              <p className="text-xl text-blue-100 leading-relaxed mb-6">
                Willkommen bei LIMA-Service in Pleinfeld, mitten im wunderschönen Fränkischen Seenland. Wir sind Ihr Spezialist für hochwertigen Textildruck und bringen Ihre kreativen Ideen professionell auf Textilien.
              </p>
              <p className="text-lg text-blue-200 leading-relaxed">
                Egal ob persönliche Projekte, Firmenbekleidung oder Event-Textilien - wir setzen Ihre Visionen mit modernster Technik und jahrelanger Erfahrung perfekt um.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-white/20 hover:bg-white/20 transition-all"
                >
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-purple-400 rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-sm text-blue-200">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                className="absolute -top-8 -right-8 w-32 h-32 bg-gradient-to-r from-blue-300/20 to-purple-300/20 rounded-full backdrop-blur-sm"
              />
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="absolute -bottom-8 -left-8 w-24 h-24 bg-gradient-to-r from-purple-300/20 to-pink-300/20 rounded-full backdrop-blur-sm"
              />
              
              <div className="relative bg-white/10 backdrop-blur-md rounded-2xl shadow-2xl p-8 border border-white/20">
                {/* Workshop image */}
                <div className="relative h-48 rounded-xl overflow-hidden mb-6">
                  <img 
                    src="https://images.unsplash.com/photo-1581833971358-2c8b550f87b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                    alt="LIMA-Service Werkstatt"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h4 className="font-bold text-lg">Moderne Werkstatt</h4>
                    <p className="text-sm text-gray-200">Pleinfeld, Fränkisches Seenland</p>
                  </div>
                </div>

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Warum LIMA-Service?
                  </h3>
                </div>

                <div className="space-y-4">
                  <motion.div
                    whileHover={{ x: 10 }}
                    className="flex items-start space-x-4 p-3 rounded-lg bg-white/5 backdrop-blur-sm"
                  >
                    <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-white mb-1">Lokale Expertise</h4>
                      <p className="text-sm text-blue-200">Verwurzelt im Fränkischen Seenland</p>
                    </div>
                  </motion.div>

                  <motion.div
                    whileHover={{ x: 10 }}
                    className="flex items-start space-x-4 p-3 rounded-lg bg-white/5 backdrop-blur-sm"
                  >
                    <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-white mb-1">Faire Preise</h4>
                      <p className="text-sm text-purple-200">Ab 3,00 € für höchste Qualität</p>
                    </div>
                  </motion.div>

                  <motion.div
                    whileHover={{ x: 10 }}
                    className="flex items-start space-x-4 p-3 rounded-lg bg-white/5 backdrop-blur-sm"
                  >
                    <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-white mb-1">Schnelle Umsetzung</h4>
                      <p className="text-sm text-green-200">Von der Idee zum fertigen Produkt</p>
                    </div>
                  </motion.div>

                  <motion.div
                    whileHover={{ x: 10 }}
                    className="flex items-start space-x-4 p-3 rounded-lg bg-white/5 backdrop-blur-sm"
                  >
                    <div className="w-2 h-2 bg-orange-400 rounded-full mt-2 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-white mb-1">Persönliche Beratung</h4>
                      <p className="text-sm text-orange-200">Individuelle Lösungen für jeden Kunden</p>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;